/* Grimorio theme — global components (vanilla, no dependencies) */

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` }
  };
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function announceToScreenReader(message) {
  const region = document.getElementById('CartLiveRegion');
  if (region) {
    region.textContent = '';
    requestAnimationFrame(() => (region.textContent = message));
  }
}

/* ---------------------------------------------------------------------------
   Drawer component — shared by menu drawer, search drawer and cart drawer.
   Markup: <drawer-component id="X" class="...">
             <div class="drawer__overlay" data-drawer-close></div>
             <div class="...__panel">...</div>
           </drawer-component>
   Openers anywhere: <button data-drawer-open="X">
--------------------------------------------------------------------------- */
class DrawerComponent extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-drawer-close]')) this.close();
    });
    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') this.close();
    });
  }

  open(opener) {
    this.opener = opener || document.activeElement;
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    const focusTarget = this.querySelector('[autofocus]') || this.querySelector('button, a, input');
    if (focusTarget) setTimeout(() => focusTarget.focus(), 120);
  }

  close() {
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (this.opener && typeof this.opener.focus === 'function') this.opener.focus();
  }
}
customElements.define('drawer-component', DrawerComponent);

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-drawer-open]');
  if (!opener) return;
  const drawer = document.getElementById(opener.getAttribute('data-drawer-open'));
  if (drawer && typeof drawer.open === 'function') {
    event.preventDefault();
    drawer.open(opener);
  }
});

/* ---------------------------------------------------------------------------
   Quantity input
--------------------------------------------------------------------------- */
class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const previousValue = this.input.value;
        if (button.name === 'plus') this.input.stepUp();
        else this.input.stepDown();
        if (previousValue !== this.input.value) {
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      })
    );
  }
}
customElements.define('quantity-input', QuantityInput);

/* ---------------------------------------------------------------------------
   Variant picker — updates hidden variant id, price, availability and URL.
   Expects a <script type="application/json" data-variants> with product
   variants and inputs (radio chips or selects) named by option position.
--------------------------------------------------------------------------- */
class VariantPicker extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('[data-variants]');
    if (!dataEl) return;
    this.variants = JSON.parse(dataEl.textContent);
    this.productForm = document.getElementById(this.dataset.formId);
    this.addEventListener('change', () => this.onVariantChange());
  }

  get selectedOptions() {
    const options = [];
    this.querySelectorAll('fieldset, .variant-picker__select').forEach((group) => {
      const checked = group.querySelector('input:checked');
      if (checked) options.push(checked.value);
      const select = group.querySelector('select');
      if (select) options.push(select.value);
    });
    return options;
  }

  onVariantChange() {
    const options = this.selectedOptions;
    const variant = this.variants.find((v) =>
      v.options.every((option, index) => option === options[index])
    );
    this.updateLabels();
    this.toggleAddButton(variant);
    if (!variant) return;

    const idInput = this.productForm
      ? this.productForm.querySelector('input[name="id"]')
      : null;
    if (idInput) idInput.value = variant.id;

    this.updatePrice(variant);
    this.updateMedia(variant);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  updateLabels() {
    this.querySelectorAll('fieldset').forEach((group) => {
      const checked = group.querySelector('input:checked');
      const valueEl = group.querySelector('[data-selected-value]');
      if (checked && valueEl) valueEl.textContent = checked.value;
    });
  }

  updatePrice(variant) {
    const priceEl = document.getElementById(`Price-${this.dataset.sectionId}`);
    if (!priceEl) return;
    const saleEl = priceEl.querySelector('[data-price]');
    const compareEl = priceEl.querySelector('[data-compare-price]');
    if (saleEl) saleEl.textContent = this.formatMoney(variant.price);
    if (compareEl) {
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      compareEl.textContent = onSale ? this.formatMoney(variant.compare_at_price) : '';
      compareEl.classList.toggle('hidden', !onSale);
    }
  }

  updateMedia(variant) {
    if (!variant.featured_media) return;
    const thumb = document.querySelector(
      `[data-media-thumb][data-media-id="${variant.featured_media.id}"]`
    );
    if (thumb) thumb.click();
  }

  toggleAddButton(variant) {
    if (!this.productForm) return;
    const button = this.productForm.querySelector('[name="add"]');
    if (!button) return;
    const text = button.querySelector('span');
    if (!variant) {
      button.setAttribute('disabled', 'disabled');
      if (text) text.textContent = window.themeStrings.unavailable;
    } else if (!variant.available) {
      button.setAttribute('disabled', 'disabled');
      if (text) text.textContent = window.themeStrings.soldOut;
    } else {
      button.removeAttribute('disabled');
      if (text) text.textContent = window.themeStrings.addToCart;
    }
  }

  formatMoney(cents) {
    const format = (window.themeSettings && window.themeSettings.moneyFormat) || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    const [units, decimals] = amount.split('.');
    const withThousands = units.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace(/\{\{\s*amount\s*\}\}/, `${withThousands}.${decimals}`)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withThousands)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${units}.${decimals}`);
  }
}
customElements.define('variant-picker', VariantPicker);

/* ---------------------------------------------------------------------------
   Product form — AJAX add to cart, opens the cart drawer.
--------------------------------------------------------------------------- */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.submitButton = this.form.querySelector('[name="add"]');
    this.form.addEventListener('submit', (event) => this.onSubmit(event));
  }

  onSubmit(event) {
    const drawer = document.getElementById('CartDrawer');
    if (!drawer || !window.themeSettings.cartDrawerEnabled) return;
    event.preventDefault();
    if (this.submitButton.getAttribute('disabled') === 'disabled') return;

    const buttonText = this.submitButton.querySelector('span');
    const originalText = buttonText ? buttonText.textContent : '';
    if (buttonText) buttonText.textContent = window.themeStrings.adding;
    this.submitButton.setAttribute('aria-busy', 'true');

    const formData = new FormData(this.form);
    formData.append('sections', 'cart-drawer');
    formData.append('sections_url', window.location.pathname);

    fetch(`${window.routes.cart_add_url}.js`, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status && response.status !== 200) {
          this.showError(response.description || window.themeStrings.cartError);
          return;
        }
        if (response.sections && response.sections['cart-drawer']) {
          drawer.renderContents(response.sections['cart-drawer']);
        }
        announceToScreenReader(window.themeStrings.itemAdded);
        drawer.open(this.submitButton);
      })
      .catch(() => this.showError(window.themeStrings.cartError))
      .finally(() => {
        this.submitButton.removeAttribute('aria-busy');
        if (buttonText) buttonText.textContent = originalText;
      });
  }

  showError(message) {
    let errorEl = this.querySelector('.product-form__error');
    if (!errorEl) {
      errorEl = document.createElement('div');
      errorEl.className = 'product-form__error form-status form-status--error';
      errorEl.setAttribute('role', 'alert');
      this.form.appendChild(errorEl);
    }
    errorEl.textContent = message;
    setTimeout(() => errorEl.remove(), 5000);
  }
}
customElements.define('product-form', ProductForm);

/* ---------------------------------------------------------------------------
   Product gallery — thumbnail switching.
--------------------------------------------------------------------------- */
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.mainImage = this.querySelector('[data-main-image]');
    this.querySelectorAll('[data-media-thumb]').forEach((thumb) =>
      thumb.addEventListener('click', () => this.setActive(thumb))
    );
  }

  setActive(thumb) {
    const img = thumb.querySelector('img');
    if (img && this.mainImage) {
      this.mainImage.src = img.dataset.fullSrc || img.src;
      this.mainImage.srcset = img.dataset.fullSrcset || '';
      this.mainImage.alt = img.alt || '';
    }
    this.querySelectorAll('[data-media-thumb]').forEach((t) => t.classList.remove('is-active'));
    thumb.classList.add('is-active');
  }
}
customElements.define('product-gallery', ProductGallery);

/* ---------------------------------------------------------------------------
   Horizontal slider (testimonials etc.)
--------------------------------------------------------------------------- */
class SliderComponent extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-slider-track]');
    if (!this.track) return;
    this.querySelectorAll('[data-slider-prev]').forEach((b) =>
      b.addEventListener('click', () => this.scrollByCard(-1))
    );
    this.querySelectorAll('[data-slider-next]').forEach((b) =>
      b.addEventListener('click', () => this.scrollByCard(1))
    );
  }

  scrollByCard(direction) {
    const card = this.track.firstElementChild;
    const amount = card ? card.getBoundingClientRect().width + 20 : 320;
    this.track.scrollBy({ left: amount * direction, behavior: 'smooth' });
  }
}
customElements.define('slider-component', SliderComponent);

/* ---------------------------------------------------------------------------
   Close facet disclosures on outside click.
--------------------------------------------------------------------------- */
document.addEventListener('click', (event) => {
  document.querySelectorAll('details.facets__disclosure[open]').forEach((details) => {
    if (!details.contains(event.target)) details.removeAttribute('open');
  });
});
